import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Search, Ticket, ChevronRight, ArrowLeft, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/lib/currencyUtils";

interface Order {
  id: string;
  reference: string;
  user_first_name: string;
  user_last_name: string;
  user_email: string;
  phone_number_complete: string | null;
  total_amount: number;
  status: string;
  created_at: string;
  vip_ticket_quantity: number | null;
  standing_tickets: number | null;
  early_bird_tickets: number | null;
  vip_seats: string[] | null;
}

interface OrdersTableProps {
  orders: Order[];
  eventTitle: string;
  eventId: string;
  currency: string;
  onSelectOrder: (order: Order) => void;
  onBack: () => void;
  isLoading?: boolean;
}

export const OrdersTable = ({ 
  orders, 
  eventTitle, 
  eventId,
  currency,
  onSelectOrder, 
  onBack,
  isLoading 
}: OrdersTableProps) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");

  const filteredOrders = orders.filter((order) => {
    const search = searchTerm.toLowerCase();
    return (
      order.reference.toLowerCase().includes(search) ||
      order.user_first_name.toLowerCase().includes(search) ||
      order.user_last_name.toLowerCase().includes(search) ||
      order.user_email.toLowerCase().includes(search)
    );
  });

  const getTicketCount = (order: Order) => {
    return (order.vip_ticket_quantity || 0) + 
           (order.standing_tickets || 0) + 
           (order.early_bird_tickets || 0);
  };

  // Calculate summary stats (only for completed/paid orders)
  const completedOrders = orders.filter(o => 
    o.status.toLowerCase() === 'completed' || o.status.toLowerCase() === 'paid'
  );
  
  const totalTicketsSold = completedOrders.reduce((sum, order) => 
    sum + getTicketCount(order), 0
  );
  
  const totalVipTickets = completedOrders.reduce((sum, order) => 
    sum + (order.vip_ticket_quantity || 0), 0
  );
  
  const totalGaTickets = completedOrders.reduce((sum, order) => 
    sum + (order.standing_tickets || 0) + (order.early_bird_tickets || 0), 0
  );
  
  const totalSales = completedOrders.reduce((sum, order) => 
    sum + order.total_amount, 0
  );

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'paid':
        return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'pending':
        return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case 'refunded':
        return 'bg-orange-500/10 text-orange-500 border-orange-500/20';
      case 'cancelled':
      case 'failed':
        return 'bg-destructive/10 text-destructive border-destructive/20';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground">Total Tickets</p>
            <p className="text-xl font-bold">{totalTicketsSold}</p>
          </div>
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground">GA Tickets</p>
            <p className="text-xl font-bold">{totalGaTickets}</p>
          </div>
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground">VIP Tickets</p>
            <p className="text-xl font-bold">{totalVipTickets}</p>
          </div>
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground">Total Sales</p>
            <p className="text-xl font-bold">{formatCurrency(totalSales, currency)}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onBack}
            className="h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <CardTitle className="text-lg">{eventTitle}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {orders.length} ticket buyer{orders.length !== 1 ? 's' : ''}
            </p>
          </div>
          <Button 
            onClick={() => navigate(`/producers/scanner?eventId=${eventId}`)}
            className="gap-2"
          >
            <QrCode className="h-4 w-4" />
            <span className="hidden sm:inline">Scan Tickets</span>
          </Button>
        </div>
        
        {orders.length > 0 && (
          <div className="relative mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, or reference..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        )}
      </CardHeader>
      
      <CardContent className="p-0 md:p-6 md:pt-0">
        {orders.length === 0 ? (
          <div className="text-center py-12 px-4">
            <div className="w-14 h-14 mx-auto rounded-full bg-muted flex items-center justify-center mb-4">
              <Ticket className="w-7 h-7 text-muted-foreground" />
            </div>
            <h3 className="font-medium text-foreground">No orders yet</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Orders will appear here once tickets are purchased
            </p>
          </div>
        ) : (
          <>
            {/* Mobile view - Card layout */}
            <div className="md:hidden divide-y divide-border">
              {filteredOrders.map((order) => (
                <div
                  key={order.id}
                  className="p-4 cursor-pointer hover:bg-accent/50 transition-colors"
                  onClick={() => onSelectOrder(order)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1 min-w-0">
                      <p className="font-medium text-foreground">
                        {order.user_first_name} {order.user_last_name}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">
                        {order.user_email}
                      </p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-xs">
                          #{order.reference}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {getTicketCount(order)} ticket{getTicketCount(order) !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <p className="font-semibold text-foreground">
                          {formatCurrency(order.total_amount, currency)}
                        </p>
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${getStatusColor(order.status)}`}
                        >
                          {order.status}
                        </Badge>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop view - Table layout */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Reference</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="text-center">Tickets</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map((order) => (
                    <TableRow
                      key={order.id}
                      className="cursor-pointer hover:bg-accent/50"
                      onClick={() => onSelectOrder(order)}
                    >
                      <TableCell className="font-mono text-sm">
                        #{order.reference}
                      </TableCell>
                      <TableCell className="font-medium">
                        {order.user_first_name} {order.user_last_name}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {order.user_email}
                      </TableCell>
                      <TableCell className="text-center">
                        {getTicketCount(order)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(order.total_amount, currency)}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge 
                          variant="outline" 
                          className={getStatusColor(order.status)}
                        >
                          {order.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {filteredOrders.length === 0 && searchTerm && (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No orders match your search</p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
